import { ReactElement, useState, useEffect } from 'react'
import { cn } from '../../../lib/utils'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import ReactDOMServer from 'react-dom/server'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../dropdown-menu'
import { Permissions } from '@shiftly/shared/src/permissions'
import { DateTime } from 'luxon'

export interface CalendarEvent {
  id: string
  start: Date
  end: Date
  [key: string]: any
}

interface BaseCalendarViewProps<T> {
  events: T[]
  className?: string
  view?: "timeGridDay" | "timeGridWeek"
  onEventClick?: (eventInfo: { event: any }) => void
  showToolbar?: boolean
  minHeight?: number
  renderEventContent: (eventInfo: { event: any }) => React.ReactNode
  eventClassNames?: (eventInfo: { event: any }) => string
  showDateNumbers?: boolean
  nowIndicator?: boolean
  hiddenDays?: number[]
  toolbar?: {
    left: string
    center: string
    right: string
  }
  slotLabelFormat?: (arg: { date: { marker: Date } }) => string
  slotLabelInterval?: string
  onDatesSet?: (arg: { start: DateTime, end: DateTime, view: any }) => void
  calendarRef?: React.RefObject<any>
  onEventEdit?: (event: T) => void
  onEventDelete?: (event: T) => void
  onMultiEventEdit?: (events: T[]) => void
  onMultiEventDelete?: (events: T[]) => void
  onCopy?: (events: T[]) => void
  onCopyTimesheet?: (events: T[]) => void
  permissions?: string[]
  titleFormat?: () => string
}

export function BaseCalendarView<T extends CalendarEvent>({ 
  events, 
  className,
  view = "timeGridWeek",
  onEventClick,
  showToolbar = true,
  renderEventContent,
  eventClassNames,
  showDateNumbers = true,
  nowIndicator = true,
  hiddenDays,
  toolbar,
  slotLabelFormat,
  slotLabelInterval = "08:00:00",
  onDatesSet,
  calendarRef,
  onEventEdit,
  onEventDelete,
  onMultiEventEdit,
  onMultiEventDelete,
  onCopy,
  onCopyTimesheet,
  permissions = [],
  titleFormat
}: BaseCalendarViewProps<T>) {
  const [selectedEvent, setSelectedEvent] = useState<T | null>(null)
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set())
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number, y: number } | null>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedEvents.size > 0) {
        setSelectedEvents(new Set())
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedEvents])

  const handleContainerClick = (e: React.MouseEvent) => {
    if (!e.ctrlKey && !e.metaKey && selectedEvents.size > 0) {
      setSelectedEvents(new Set())
    }
  }

  const handleEventClick = (info: { event: any, jsEvent: MouseEvent }) => {
    info.jsEvent.stopPropagation() // Prevent the container click from firing
    const event = events.find(e => e.id === info.event.id)
    if (!event) return
    
    // Handle multi-select with Ctrl/Cmd key
    if (info.jsEvent.ctrlKey || info.jsEvent.metaKey) {
      const newSelectedEvents = new Set(selectedEvents)
      if (newSelectedEvents.has(event.id)) {
        newSelectedEvents.delete(event.id)
      } else {
        newSelectedEvents.add(event.id)
      }
      setSelectedEvents(newSelectedEvents)
      
      // Force unselect any FullCalendar selection
      if (calendarRef?.current) {
        calendarRef.current.getApi().unselect()
      }
      return
    }

    // Clear selections if Ctrl is not pressed
    if (selectedEvents.size > 0) {
      setSelectedEvents(new Set())
    }
    
    // Handle normal click
    setSelectedEvent(event === selectedEvent ? null : event)
    onEventClick?.({ event: { extendedProps: event } })
    
    // Force unselect any FullCalendar selection
    if (calendarRef?.current) {
      calendarRef.current.getApi().unselect()
    }
  }

  const handleEventContextMenu = (info: { event: any, el: HTMLElement, jsEvent: MouseEvent }) => {
    info.jsEvent.preventDefault()
    const event = events.find(e => e.id === info.event.id)
    if (!event) return

    setSelectedEvent(event)
    setContextMenuPosition({ x: info.jsEvent.clientX, y: info.jsEvent.clientY })
  }

  const handleCloseContextMenu = () => {
    setSelectedEvent(null)
    setContextMenuPosition(null)
  }

  const getComputedEventClassNames = (arg: { event: any }) => {
    const baseClasses = eventClassNames?.({ event: arg.event }) || ''
    const isSelected = selectedEvents.has(arg.event.id)
    return cn(
      baseClasses,
      isSelected && 'selected-event'
    )
  }

  return (
    <div className={cn("relative p-6", className)} onClick={handleContainerClick}>
      <FullCalendar
        ref={calendarRef}
        plugins={[
          timeGridPlugin, 
          interactionPlugin
        ]}
        timeZone="Europe/London"
        now={function() {
          return DateTime.now().setZone('Europe/London').toFormat("yyyy-MM-dd HH:mm:ss");
        }}
        initialView={view}
        firstDay={1}
        selectable={false}
        unselectAuto={true}
        eventInteractive={false}
        buttonText={{
          today: 'Today'
        }}
        headerToolbar={showToolbar ? toolbar : false}
        titleFormat={(date) => {
          if (!date.start?.marker || !date.end?.marker) return '';
          const start = DateTime.fromJSDate(date.start.marker).setZone('Europe/London');
          const end = DateTime.fromJSDate(date.end.marker).setZone('Europe/London').minus({ days: 1 });
          return `${start.toFormat('d MMM')} - ${end.toFormat('d MMM')} ${start.toFormat('yyyy')}`;
        }}
        events={events}
        contentHeight={500}
        eventClick={handleEventClick}
        eventDidMount={(info) => {
          info.el.addEventListener('contextmenu', (e) => handleEventContextMenu({ event: info.event, el: info.el, jsEvent: e }))
        }}
        slotMinTime="06:00:00"
        slotMaxTime="30:00:00"
        expandRows={true}
        stickyHeaderDates={true}
        nowIndicator={nowIndicator}
        slotDuration={slotLabelInterval}
        slotLabelInterval={slotLabelInterval}
        slotLabelFormat={slotLabelFormat}
        allDaySlot={false}
        dayHeaderFormat={{
          weekday: 'short',
          day: 'numeric',
          omitCommas: true
        }}
        views={{
          timeGridWeek: {
            dayHeaderFormat: { weekday: 'short', day: showDateNumbers ? 'numeric' : undefined },
            dayHeaderContent: titleFormat || undefined
          },
          timeGridDay: {
            dayHeaderFormat: { weekday: 'long', day: showDateNumbers ? 'numeric' : undefined, month: 'long' }
          },
          timeGridMonth: {
            type: 'timeGrid',
            duration: { months: 1 },
            dayMinWidth: 150,
            buttonText: 'timeline',
            dayHeaderFormat: {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
              omitCommas: true
            },
            eventMaxStack: 6,
            eventContent: (arg) => {
              const content = renderEventContent({ event: arg.event });
              return {
                html: `<div class="event-content-wrapper">${
                  typeof content === 'string' ? content : ReactDOMServer.renderToString(content as ReactElement)
                }</div>`
              };
            }
          }
        }}
        eventClassNames={getComputedEventClassNames}
        eventContent={(arg) => renderEventContent({ event: arg.event })}
        dayMaxEvents={true}
        hiddenDays={hiddenDays}
        datesSet={onDatesSet ? (arg) => onDatesSet({ start: DateTime.fromISO(arg.start.toISOString()), end: DateTime.fromISO(arg.end.toISOString()), view: arg.view }) : undefined}
      />

      {(selectedEvent || selectedEvents.size > 0) && contextMenuPosition && (
        <DropdownMenu open onOpenChange={handleCloseContextMenu}>
          <DropdownMenuTrigger asChild>
            <div 
              style={{ 
                position: 'fixed', 
                left: contextMenuPosition.x, 
                top: contextMenuPosition.y,
                width: 1,
                height: 1,
                padding: 0
              }} 
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {onCopy && (
              <DropdownMenuItem onClick={() => {
                if (selectedEvents.size > 1) {
                  onCopy(Array.from(selectedEvents).map(id => events.find(e => e.id === id) as T))
                } else if (onCopy && selectedEvent) {
                  onCopy([selectedEvent])
                }
                handleCloseContextMenu()
              }}>
                  Copy as Text
              </DropdownMenuItem>
            )}
            {onCopyTimesheet && (
              <DropdownMenuItem onClick={() => {
                onCopyTimesheet(Array.from(selectedEvents).map(id => events.find(e => e.id === id) as T))
                handleCloseContextMenu()
              }}>
                Copy as Timesheet
              </DropdownMenuItem>
            )}
            {permissions.includes(Permissions.SHIFTS_EDIT) && (
              <>
                {selectedEvents.size >= 1 && onMultiEventEdit ? (
                  <DropdownMenuItem onClick={() => {
                    const selectedItems = events.filter(e => selectedEvents.has(e.id))
                    onMultiEventEdit(selectedItems)
                    handleCloseContextMenu()
                  }}>
                    Edit {selectedEvents.size} Events
                  </DropdownMenuItem>
                ) : onEventEdit && selectedEvent && selectedEvents.size === 0 && (
                  <DropdownMenuItem onClick={() => {
                    onEventEdit(selectedEvent)
                    handleCloseContextMenu()
                  }}>
                    Edit
                  </DropdownMenuItem>
                )}
              </>
            )}
            {permissions.includes(Permissions.SHIFTS_DELETE) && (
              <>
                {selectedEvents.size > 1 && onMultiEventDelete ? (
                  <DropdownMenuItem 
                    onClick={() => {
                      const selectedItems = events.filter(e => selectedEvents.has(e.id))
                      onMultiEventDelete(selectedItems)
                      handleCloseContextMenu()
                    }}
                    className="text-brand-red dark:text-brand-red"
                  >
                    Delete {selectedEvents.size} Events
                  </DropdownMenuItem>
                ) : onEventDelete && selectedEvent && (
                  <DropdownMenuItem 
                    onClick={() => {
                      onEventDelete(selectedEvent)
                      handleCloseContextMenu()
                    }}
                    className="text-brand-red dark:text-brand-red"
                  >
                    Delete
                  </DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
} 